import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import { DashboardWorksiteDistribution } from '../dashboardTypes'
import {
  complianceStatusLabel,
  formatWorksiteEnrollment,
  GeographicView,
  worksiteDotColor,
  worksiteProgramSummary,
  worksitesForGeographicView,
} from './worksiteDistribution'

const Section = styled.div`
  padding: 1rem 1.5rem 0.25rem;
`

const Heading = styled(Text)`
  display: block;
  margin-bottom: 0.75rem;
  color: ${(props) => props.theme.palette.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.6875rem;
`

const Row = styled.button`
  display: grid;
  grid-template-columns: 0.75rem minmax(0, 0.9fr) minmax(0, 1.4fr) auto;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  padding: 0.55rem 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`

const Dot = styled.span<{ $color: string }>`
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: ${(props) => props.$color};
`

const Programs = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(props) => props.theme.palette.text.secondary};
`

interface WorksiteDistributionListProps {
  sites: DashboardWorksiteDistribution[]
  view: GeographicView
  onSiteClick: (locationKey: string) => void
}

const WorksiteDistributionList = ({
  sites,
  view,
  onSiteClick,
}: WorksiteDistributionListProps) => {
  const visible = worksitesForGeographicView(sites, view)
  const rows =
    view === 'compliance'
      ? [...visible].sort(
          (a, b) =>
            Number(a.compliant) - Number(b.compliant) ||
            a.label.localeCompare(b.label)
        )
      : visible

  return (
    <Section>
      <Heading variant='caption'>
        {view === 'compliance' ? 'Compliance status' : 'Top sites by enrollment'}
      </Heading>
      {rows.length === 0 ? (
        <Text variant='body2' color={palette.text.secondary}>
          No worksites yet
        </Text>
      ) : (
        rows.map((site) => (
          <Row
            key={site.id}
            type='button'
            disabled={view === 'offices' && !site.locationKey}
            onClick={() => onSiteClick(site.locationKey || '')}
          >
            <Dot $color={worksiteDotColor(site, view)} />
            <Text variant='action4'>{site.label}</Text>
            {view === 'compliance' ? (
              <Text
                variant='action4'
                style={{
                  gridColumn: '3 / span 2',
                  color: worksiteDotColor(site, view),
                }}
              >
                {complianceStatusLabel(site.compliant)}
              </Text>
            ) : (
              <>
                <Programs variant='body2'>
                  {worksiteProgramSummary(site.programs)}
                </Programs>
                <Text variant='action4'>
                  {formatWorksiteEnrollment(site.enrolled)}
                </Text>
              </>
            )}
          </Row>
        ))
      )}
    </Section>
  )
}

export default React.memo(WorksiteDistributionList)
